import React, { useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { Card } from "@shared/ui/card";
import { ChannelPerformanceProps, ChannelStat } from "../../types";
import {
  CHANNEL_CONFIG,
  periodLabels,
  transformChannelData,
} from "../../utils";
import { SectionWrapper } from "../shared/sectionWrapper";
import { MetricCard } from "../shared/metricCard";
import { EmptyState } from "../shared/emptyState";

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;

  const data = payload[0];
  return (
    <div className="bg-background border rounded-lg p-2 shadow-sm">
      <p className="text-sm font-medium">{data.name}</p>
      <p className="text-sm text-muted-foreground">
        {data.value.toLocaleString()}
      </p>
    </div>
  );
};

const ChannelChart = ({
  title,
  channelType,
  data,
  dataKey,
}: {
  title: string;
  channelType: string;
  data: any[] | ChannelStat;
  dataKey?: string;
}) => {
  const chartData = useMemo(() => {
    if (!Array.isArray(data)) return null;

    const channelStats = data.filter(
      (item) => item.channelType === (dataKey || channelType)
    );

    if (channelStats.length === 0) return null;

    const transformedData = transformChannelData(channelType, channelStats[0]);
    return transformedData.filter((item) => item.value > 0);
  }, [data, channelType, dataKey]);

  const getEmptyMessage = () => {
    if (!Array.isArray(data)) return "Invalid data";
    if (!chartData)
      return `No ${title
        .toLowerCase()
        .replace(" performance", "")} data available`;
    if (chartData.length === 0)
      return `No ${title
        .toLowerCase()
        .replace(" performance", "")} data available`;
    return "";
  };

  const emptyMessage = getEmptyMessage();
  if (emptyMessage) {
    return (
      <Card className="">
        <h4 className="mb-2 card-heading-text">{title}</h4>
        <div className="h-[120px]">
          <EmptyState size="sm" />
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-3">
      <h4 className="mb-2 text-xs font-medium">{title}</h4>
      <div className="h-[120px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={25}
              outerRadius={40}
            >
              {chartData!.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};

const OverviewCards = ({ overview }: { overview: any[] }) => {
  if (!Array.isArray(overview)) return null;

  return (
    <div className="grid grid-cols-3 gap-4 mb-6">
      {overview.map((item, index) => (
        <MetricCard
          position="top"
          key={index}
          title={periodLabels[item.period] || item.period}
          value={item.totalMessages?.toLocaleString() || "0"}
          variant="white"
          size="sm"
        />
      ))}
    </div>
  );
};

export const ChannelPerformance: React.FC<ChannelPerformanceProps> = ({
  data,
  overview,
}) => {
  return (
    <SectionWrapper
      title="Channel Performance"
      isEmpty={!data && !overview}
      loadingType="charts"
      loadingCount={4}
      className="card-heading-text"
    >
      {/* Overview Cards */}
      {Array.isArray(overview) && <OverviewCards overview={overview} />}

      {/* Channel Performance Charts */}
      <div className="grid grid-cols-2 gap-4">
        {Object.entries(CHANNEL_CONFIG).map(([key, config]) => (
          <ChannelChart
            key={key}
            title={config.title}
            channelType={config.type}
            data={data}
            dataKey={config.dataType}
          />
        ))}
      </div>
    </SectionWrapper>
  );
};
